import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Grid,
  Avatar,
  Chip,
  Button,
  Divider,
  Checkbox,
  Switch,
  FormControlLabel,
  Autocomplete,
  TextField,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import EventNoteIcon from '@mui/icons-material/EventNote';
import CakeOutlinedIcon from '@mui/icons-material/CakeOutlined';
import WorkHistoryOutlinedIcon from '@mui/icons-material/WorkHistoryOutlined';
import EmailIcon from '@mui/icons-material/Email';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AppLayout from '../components/AppLayout';
import ProfileHelpSidebar from '../components/ProfileHelpSidebar';
import { apiService } from '../services/api';
import { API_ENDPOINTS, EXPENSE_CLAIMS_ENABLED } from '../constants/api';
import type { EmployeeCheckInConfig } from '../types';
import { RoleTemplateState } from '../types';
import { formatDate } from '../utils/dateFormat';
import { getRoleBadgeColor } from '../utils/roleBadge';
import { useAuth } from '../contexts/AuthContext';
import { useAgentContext } from '../contexts/AgentContext';
import { usePageAgentContext } from '../hooks/usePageAgentContext';
import type { AxiosError } from 'axios';
import { getApiErrorMessage } from '../utils/apiError';
import { couldNotUpdate, couldNotSave, couldNotAcknowledge, genericCopy, displayTimeZoneCopy } from '../utils/userFacingCopy';
import { savedSuccessfully } from '../utils/successCopy';
import { useSuccessSnackbar } from '../contexts/SuccessSnackbarContext';
import { applyResolvedDisplayTimeZone, loadDateLocaleFromSystem } from '../services/dateLocaleService';
import { getDeviceIanaTimeZone, listIanaTimeZones, ORGANISATION_TIME_ZONE_VALUE } from '../utils/timeZones';
import {
  employeeManagerOptedOutMessage,
  employeeManagerOptedOutShortLabel,
} from '../utils/checkInOptOutCopy';
import { emailOptOutAcknowledgmentText } from '../utils/emailOptOutCopy';

interface ObjectiveMeasure {
  id: string;
  name: string;
}

interface BehaviouralMeasure {
  id: string;
  name: string;
}

interface RoleTemplate {
  id: string;
  name: string;
  description: string;
  state?: RoleTemplateState;
  lastEditedBy?: string;
  lastEditedAt?: string;
  lastApprovedBy?: string;
  lastApprovedAt?: string;
  objectiveMeasures?: ObjectiveMeasure[];
  behaviouralMeasures?: BehaviouralMeasure[];
}

interface ManagerOption {
  id: string;
  fullName: string;
  email: string;
}

interface UserDetails {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  isActive: boolean;
  managerId?: string;
  roleTemplateId?: string;
  pendingRoleTemplateId?: string;
  roleTemplateChangeManagerAcknowledgedAt?: string;
  roleTemplateChangeEmployeeAcknowledgedAt?: string;
  departmentId?: string; // Deprecated - use departmentIds
  departmentIds?: string[];
  companyName?: string;
  businessUnitName?: string;
  departmentName?: string; // Deprecated - use departmentNames
  departmentNames?: string[];
  lastLoginAt?: string;
  dateOfBirth?: string;
  dateOfEngagement?: string;
  preferredIanaTimeZoneId?: string | null;
}

interface EmailPreferences {
  receiveReviewEmails: boolean;
  receiveCheckInEmails: boolean;
  receiveIdpEmails: boolean;
  receiveProfileChangeEmails: boolean;
  receiveExpenseClaimEmails: boolean;
  receiveAllEmails: boolean;
  acknowledgmentText?: string;
  emailOptOutAgreedAt?: string | null;
  mustReaffirmAcknowledgment?: boolean;
}

export default function ProfilePage() {
  const { user: authUser, refreshUser, displayTimeZoneId } = useAuth();
  const { showLegacyHelp } = useAgentContext();
  const showSuccess = useSuccessSnackbar();
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [roleTemplate, setRoleTemplate] = useState<RoleTemplate | null>(null);
  const [pendingRoleTemplate, setPendingRoleTemplate] = useState<RoleTemplate | null>(null);
  const [manager, setManager] = useState<ManagerOption | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [updating, setUpdating] = useState(false);
  const [savingProfileDates, setSavingProfileDates] = useState(false);
  const [savingTimeZone, setSavingTimeZone] = useState(false);
  const [organisationTimeZoneId, setOrganisationTimeZoneId] = useState('Africa/Johannesburg');
  const [selectedTimeZoneId, setSelectedTimeZoneId] = useState(ORGANISATION_TIME_ZONE_VALUE);
  const [savedTimeZoneId, setSavedTimeZoneId] = useState(ORGANISATION_TIME_ZONE_VALUE);
  const [dateOfBirthInput, setDateOfBirthInput] = useState('');
  const [dateOfEngagementInput, setDateOfEngagementInput] = useState('');
  const [checkInConfig, setCheckInConfig] = useState<EmployeeCheckInConfig | null>(null);
  const [emailPreferences, setEmailPreferences] = useState<EmailPreferences>({
    receiveReviewEmails: true,
    receiveCheckInEmails: true,
    receiveIdpEmails: true,
    receiveProfileChangeEmails: true,
    receiveExpenseClaimEmails: true,
    receiveAllEmails: true,
  });
  /** Last saved "receive all" from the server — used to require ack only when newly turning emails off. */
  const [savedReceiveAllEmails, setSavedReceiveAllEmails] = useState(true);
  const [agreeToOptOutTerms, setAgreeToOptOutTerms] = useState(false);
  const [helpExpanded, setHelpExpanded] = useState(false);
  usePageAgentContext({
    pageId: 'profile',
    pageTitle: 'Profile',
    visibleSections: ['personal-details', 'role-template', 'display-timezone', 'email-preferences'],
    suggestedPrompts: [
      'How do my email preferences work?',
      'What profile fields can I edit here?',
      'How do times on screen work?',
      'Explain the role template section.',
    ],
  });

  useEffect(() => {
    const loadProfileData = async () => {
      if (!authUser) return;

      try {
        setLoading(true);
        setError('');

        // Load current user details
        const userResponse = await apiService.get<UserDetails>(API_ENDPOINTS.USERS.ME);
        const user = userResponse.data;
        setUserDetails(user);
        setDateOfBirthInput(user.dateOfBirth ? user.dateOfBirth.slice(0, 10) : '');
        setDateOfEngagementInput(user.dateOfEngagement ? user.dateOfEngagement.slice(0, 10) : '');
        const preferred = user.preferredIanaTimeZoneId?.trim() || ORGANISATION_TIME_ZONE_VALUE;
        setSelectedTimeZoneId(preferred);
        setSavedTimeZoneId(preferred);

        try {
          const locale = await loadDateLocaleFromSystem();
          setOrganisationTimeZoneId(locale.ianaTimeZoneId);
        } catch {
          // Keep Johannesburg fallback
        }

        // Load role template if user has one
        if (user.roleTemplateId) {
          try {
            const roleResponse = await apiService.get<RoleTemplate>(`${API_ENDPOINTS.ROLES.BASE}/${user.roleTemplateId}`);
            setRoleTemplate(roleResponse.data);
          } catch (err) {
            console.warn('Failed to load role template:', err);
          }
        }

        // Load pending role template if there's a pending change
        if (user.pendingRoleTemplateId) {
          try {
            const pendingRoleResponse = await apiService.get<RoleTemplate>(`${API_ENDPOINTS.ROLES.BASE}/${user.pendingRoleTemplateId}`);
            setPendingRoleTemplate(pendingRoleResponse.data);
          } catch (err) {
            console.warn('Failed to load pending role template:', err);
          }
        } else {
          setPendingRoleTemplate(null);
        }

        // Load managers list and resolve current manager
        const managersResponse = await apiService.get<ManagerOption[]>(API_ENDPOINTS.USERS.MANAGERS);
        if (user.managerId) {
          const currentManager = managersResponse.data.find((m) => m.id === user.managerId);
          setManager(currentManager ?? null);
        } else {
          setManager(null);
        }

        // Load check-in configuration
        try {
          const configResponse = await apiService.get<EmployeeCheckInConfig>(API_ENDPOINTS.CHECK_INS.CONFIG_MY);
          setCheckInConfig(configResponse.data);
        } catch (err: unknown) {
          // Not found is OK - means no check-in config set yet
          if ((err as AxiosError)?.response?.status !== 404) {
            console.warn('Failed to load check-in configuration:', err);
          }
          setCheckInConfig(null);
        }

        // Load email preferences
        try {
          const prefsResponse = await apiService.get<EmailPreferences>(API_ENDPOINTS.USERS.EMAIL_PREFERENCES);
          const p = prefsResponse.data;
          const receiveAll = p.receiveAllEmails ?? true;
          setEmailPreferences({
            receiveReviewEmails: p.receiveReviewEmails ?? true,
            receiveCheckInEmails: p.receiveCheckInEmails ?? true,
            receiveIdpEmails: p.receiveIdpEmails ?? true,
            receiveProfileChangeEmails: p.receiveProfileChangeEmails ?? true,
            receiveExpenseClaimEmails: p.receiveExpenseClaimEmails ?? true,
            receiveAllEmails: receiveAll,
            acknowledgmentText: p.acknowledgmentText,
            emailOptOutAgreedAt: p.emailOptOutAgreedAt,
            mustReaffirmAcknowledgment: p.mustReaffirmAcknowledgment,
          });
          setSavedReceiveAllEmails(receiveAll);
        } catch (err: unknown) {
          if ((err as AxiosError)?.response?.status !== 404) {
            console.warn('Failed to load email preferences:', err);
          }
        }
      } catch (err: unknown) {
        console.error('Failed to load profile data:', err);
        setError(genericCopy.profile);
      } finally {
        setLoading(false);
      }
    };

    loadProfileData();
  }, [authUser]);

  const handleProfileDatesUpdate = async () => {
    if (!authUser) return;

    try {
      setSavingProfileDates(true);
      setError('');

      const payload: { dateOfBirth?: string; dateOfEngagement?: string } = {};
      if (dateOfBirthInput.trim()) {
        payload.dateOfBirth = dateOfBirthInput;
      }
      if (dateOfEngagementInput.trim()) {
        payload.dateOfEngagement = dateOfEngagementInput;
      }

      const response = await apiService.put<UserDetails>(API_ENDPOINTS.USERS.ME, payload);
      const updatedUser = response.data;
      setUserDetails(updatedUser);
      setDateOfBirthInput(updatedUser.dateOfBirth ? updatedUser.dateOfBirth.slice(0, 10) : '');
      setDateOfEngagementInput(updatedUser.dateOfEngagement ? updatedUser.dateOfEngagement.slice(0, 10) : '');
    } catch (err: unknown) {
      console.error('Failed to update profile dates:', err);
      setError(getApiErrorMessage(err, couldNotUpdate('your profile details')));
    } finally {
      setSavingProfileDates(false);
    }
  };

  const timeZoneOptions = [
    {
      id: ORGANISATION_TIME_ZONE_VALUE,
      label: displayTimeZoneCopy.organisationOption(organisationTimeZoneId),
    },
    ...listIanaTimeZones().map((id) => ({ id, label: id })),
  ];
  if (selectedTimeZoneId && !timeZoneOptions.some((option) => option.id === selectedTimeZoneId)) {
    timeZoneOptions.push({ id: selectedTimeZoneId, label: selectedTimeZoneId });
  }
  const selectedTimeZoneOption =
    timeZoneOptions.find((option) => option.id === selectedTimeZoneId) ??
    (selectedTimeZoneId
      ? { id: selectedTimeZoneId, label: selectedTimeZoneId }
      : timeZoneOptions[0]);
  const timeZoneDirty = selectedTimeZoneId !== savedTimeZoneId;

  const handleDisplayTimeZoneSave = async () => {
    if (!authUser) return;

    try {
      setSavingTimeZone(true);
      setError('');
      const preferredIanaTimeZoneId = selectedTimeZoneId.trim() ? selectedTimeZoneId : null;
      const response = await apiService.put<UserDetails>(API_ENDPOINTS.USERS.DISPLAY_TIMEZONE, {
        preferredIanaTimeZoneId,
      });
      const updatedUser = response.data;
      setUserDetails(updatedUser);
      const saved = updatedUser.preferredIanaTimeZoneId?.trim() || ORGANISATION_TIME_ZONE_VALUE;
      setSelectedTimeZoneId(saved);
      setSavedTimeZoneId(saved);
      await applyResolvedDisplayTimeZone(updatedUser.preferredIanaTimeZoneId);
      await refreshUser();
      showSuccess(savedSuccessfully('Your time zone'));
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, couldNotSave('your time zone')));
    } finally {
      setSavingTimeZone(false);
    }
  };

  const handleUseDeviceTimeZone = () => {
    const deviceZone = getDeviceIanaTimeZone();
    if (deviceZone) {
      setSelectedTimeZoneId(deviceZone);
    }
  };

  const handleEmailPreferencesUpdate = async () => {
    if (!authUser) return;

    const turningOffAllEmails = !emailPreferences.receiveAllEmails && savedReceiveAllEmails;
    if (turningOffAllEmails && !agreeToOptOutTerms) {
      setError('Confirm the acknowledgment to turn off all optional emails.');
      return;
    }

    try {
      setUpdating(true);
      setError('');

      const response = await apiService.put<EmailPreferences>(API_ENDPOINTS.USERS.EMAIL_PREFERENCES, {
        receiveReviewEmails: emailPreferences.receiveReviewEmails,
        receiveCheckInEmails: emailPreferences.receiveCheckInEmails,
        receiveIdpEmails: emailPreferences.receiveIdpEmails,
        receiveProfileChangeEmails: emailPreferences.receiveProfileChangeEmails,
        receiveExpenseClaimEmails: emailPreferences.receiveExpenseClaimEmails,
        receiveAllEmails: emailPreferences.receiveAllEmails,
        agreeToOptOutTerms: turningOffAllEmails ? true : undefined,
      });

      const p = response.data;
      const receiveAll = p?.receiveAllEmails ?? emailPreferences.receiveAllEmails;
      setEmailPreferences((prev) => ({
        ...prev,
        ...p,
        receiveAllEmails: receiveAll,
        acknowledgmentText: p?.acknowledgmentText ?? prev.acknowledgmentText,
        emailOptOutAgreedAt: p?.emailOptOutAgreedAt ?? (receiveAll ? null : prev.emailOptOutAgreedAt),
      }));
      setSavedReceiveAllEmails(receiveAll);
      setAgreeToOptOutTerms(false);
    } catch (err: unknown) {
      console.error('Failed to update email preferences:', err);
      setError(getApiErrorMessage(err, couldNotUpdate('your email preferences')));
    } finally {
      setUpdating(false);
    }
  };

  // Acknowledgment workflow still exists in backend but UI is removed - events appear in audit log
  const handleAcknowledgeRoleTemplateChange = async () => {
    if (!userDetails) return;

    try {
      setUpdating(true);
      setError('');

      await apiService.post(API_ENDPOINTS.USERS.ACKNOWLEDGE_ROLE_TEMPLATE_CHANGE_AS_EMPLOYEE);

      // Refresh user data
      const userResponse = await apiService.get<UserDetails>(API_ENDPOINTS.USERS.ME);
      const updatedUser = userResponse.data;
      setUserDetails(updatedUser);

      // Reload role template if it changed
      if (updatedUser.roleTemplateId) {
        try {
          const roleResponse = await apiService.get<RoleTemplate>(`${API_ENDPOINTS.ROLES.BASE}/${updatedUser.roleTemplateId}`);
          setRoleTemplate(roleResponse.data);
        } catch (err) {
          console.warn('Failed to load role template:', err);
        }
      }

      // Refresh auth context
      await refreshUser();
    } catch (err: unknown) {
      console.error('Failed to acknowledge role template change:', err);
      setError(getApiErrorMessage(err, couldNotAcknowledge('the role template change')));
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </AppLayout>
    );
  }

  if (!userDetails) {
    return (
      <AppLayout>
        <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
          <Alert severity="error">{genericCopy.profile}</Alert>
        </Box>
      </AppLayout>
    );
  }

  const originalDateOfBirthInput = userDetails.dateOfBirth ? userDetails.dateOfBirth.slice(0, 10) : '';
  const originalDateOfEngagementInput = userDetails.dateOfEngagement ? userDetails.dateOfEngagement.slice(0, 10) : '';
  const hasProfileDateChanges =
    dateOfBirthInput !== originalDateOfBirthInput ||
    dateOfEngagementInput !== originalDateOfEngagementInput;

  return (
    <AppLayout>
      <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <PersonIcon sx={{ fontSize: 32, color: 'primary.main', alignSelf: 'flex-start', mt: 0.5 }} />
          <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 0 }}>
            Profile
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          Manage your personal profile information, view your assigned role template, and manage your email preferences.
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Pending Role Template Change - Manager Acknowledgment (for managers acknowledging their own change) */}
        {/* Check RoleTemplate.State instead of user fields */}
        {(() => {
          // State comes from API as string 'Approved', but enum is numeric 3
          const stateIsApproved = pendingRoleTemplate?.state === RoleTemplateState.Approved ||
                                 pendingRoleTemplate?.state === 'Approved' ||
                                 pendingRoleTemplate?.state === 3;

          return userDetails?.pendingRoleTemplateId &&
                 stateIsApproved &&
                 (authUser?.roles.includes('Manager') || authUser?.roles.includes('SeniorManager') || authUser?.roles.includes('Administrator')) &&
                 !userDetails?.roleTemplateChangeManagerAcknowledgedAt &&
                 !userDetails?.roleTemplateChangeEmployeeAcknowledgedAt;
        })() && (
          <Alert
            severity="warning"
            sx={{ mb: 3 }}
            icon={<AssignmentIcon />}
            action={
              <Button
                variant="contained"
                color="primary"
                size="medium"
                onClick={handleAcknowledgeRoleTemplateChange}
                disabled={updating}
                sx={{
                  fontWeight: 600,
                  textTransform: 'none',
                  px: 3,
                  py: 1
                }}
              >
                {updating ? <CircularProgress size={20} /> : 'Acknowledge Change'}
              </Button>
            }
          >
            <Typography variant="body1" fontWeight="600" gutterBottom>
              Role Template Change Requires Your Acknowledgment
            </Typography>
            <Typography variant="body2">
              Your role template has been changed to <strong>{pendingRoleTemplate?.name || 'Unknown'}</strong> and approved.
              Please review the template details below and acknowledge this change to complete the process.
            </Typography>
          </Alert>
        )}

        {/* Pending Role Template Change - Employee Acknowledgment (for employees after manager has acknowledged) */}
        {/* Check RoleTemplate.State instead of user fields */}
        {(() => {
          // State comes from API as string 'Approved', but enum is numeric 3
          const stateIsApproved = pendingRoleTemplate?.state === RoleTemplateState.Approved ||
                                 pendingRoleTemplate?.state === 'Approved' ||
                                 pendingRoleTemplate?.state === 3;

          return userDetails?.pendingRoleTemplateId &&
                 stateIsApproved &&
                 userDetails?.roleTemplateChangeManagerAcknowledgedAt &&
                 !userDetails?.roleTemplateChangeEmployeeAcknowledgedAt;
        })() && (
          <Alert
            severity="warning"
            sx={{ mb: 3 }}
            icon={<AssignmentIcon />}
            action={
              <Button
                variant="contained"
                color="primary"
                size="medium"
                onClick={handleAcknowledgeRoleTemplateChange}
                disabled={updating}
                sx={{
                  fontWeight: 600,
                  textTransform: 'none',
                  px: 3,
                  py: 1
                }}
              >
                {updating ? <CircularProgress size={20} /> : 'Acknowledge Change'}
              </Button>
            }
          >
            <Typography variant="body1" fontWeight="600" gutterBottom>
              Role Template Change Requires Your Acknowledgment
            </Typography>
            <Typography variant="body2">
              Your role template is being changed to <strong>{pendingRoleTemplate?.name || 'Unknown'}</strong> and your manager has acknowledged it.
              Please review the template details below and acknowledge this change to complete the process.
            </Typography>
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Profile Information Card */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      bgcolor: 'primary.main',
                      fontSize: '2rem',
                      fontWeight: 'bold',
                    }}
                  >
                    {userDetails.firstName.charAt(0)}{userDetails.lastName.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight="600">
                      {userDetails.firstName} {userDetails.lastName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {userDetails.email}
                    </Typography>
                    {userDetails.roles.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        {userDetails.roles.map((role) => (
                          <Chip
                            key={role}
                            label={role}
                            size="small"
                            color={getRoleBadgeColor(role)}
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    )}
                    {userDetails.lastLoginAt && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }} key={displayTimeZoneId}>
                        Last signed in: {formatDate(userDetails.lastLoginAt, { includeTime: true })}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Grid container spacing={3}>
                  {/* Role Template */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <BusinessIcon color="primary" fontSize="small" />
                      <Typography variant="subtitle2" color="text.secondary" fontWeight="500">
                        Role Template
                      </Typography>
                    </Box>
                    {roleTemplate ? (
                      <Typography variant="body1" fontWeight="500">
                        {roleTemplate.name}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        Not assigned
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Contact your manager or administrator to change your role template
                    </Typography>
                  </Grid>

                  {/* Manager (read-only; only admins can change via User Management) */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <SupervisorAccountIcon color="primary" fontSize="small" />
                      <Typography variant="subtitle2" color="text.secondary" fontWeight="500">
                        Manager
                      </Typography>
                    </Box>
                    {manager ? (
                      <Typography variant="body1" fontWeight="500">
                        {manager.fullName}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        Not assigned
                      </Typography>
                    )}
                  </Grid>

                  {/* Company */}
                  {userDetails.companyName && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <BusinessIcon color="primary" fontSize="small" />
                        <Typography variant="subtitle2" color="text.secondary" fontWeight="500">
                          Company
                        </Typography>
                      </Box>
                      <Typography variant="body1" fontWeight="500">
                        {userDetails.companyName}
                      </Typography>
                    </Grid>
                  )}

                  {/* Business Unit */}
                  {userDetails.businessUnitName && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <BusinessIcon color="primary" fontSize="small" />
                        <Typography variant="subtitle2" color="text.secondary" fontWeight="500">
                          Business Unit
                        </Typography>
                      </Box>
                      <Typography variant="body1" fontWeight="500">
                        {userDetails.businessUnitName}
                      </Typography>
                    </Grid>
                  )}

                  {/* Department (read-only; only admins can change via User Management) */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <BusinessIcon color="primary" fontSize="small" />
                      <Typography variant="subtitle2" color="text.secondary" fontWeight="500">
                        Department
                      </Typography>
                    </Box>
                    {userDetails.departmentNames && userDetails.departmentNames.length > 0 ? (
                      <Typography variant="body1" fontWeight="500">
                        {userDetails.departmentNames.join(', ')}
                      </Typography>
                    ) : userDetails.departmentName ? (
                      <Typography variant="body1" fontWeight="500">
                        {userDetails.departmentName}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        Not assigned
                      </Typography>
                    )}
                  </Grid>

                  {/* Date of birth (editable by user) */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <CakeOutlinedIcon color="primary" fontSize="small" />
                      <Typography variant="subtitle2" color="text.secondary" fontWeight="500">
                        Date of birth
                      </Typography>
                    </Box>
                    <TextField
                      fullWidth
                      type="date"
                      size="small"
                      value={dateOfBirthInput}
                      onChange={(e) => setDateOfBirthInput(e.target.value)}
                      slotProps={{ inputLabel: { shrink: true } }}
                      sx={{
                        '& input::-webkit-calendar-picker-indicator': {
                          cursor: 'pointer',
                        },
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Used for birthdays and personal milestone reminders.
                    </Typography>
                  </Grid>

                  {/* Date of engagement (editable by user) */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <WorkHistoryOutlinedIcon color="primary" fontSize="small" />
                      <Typography variant="subtitle2" color="text.secondary" fontWeight="500">
                        Date of engagement
                      </Typography>
                    </Box>
                    <TextField
                      fullWidth
                      type="date"
                      size="small"
                      value={dateOfEngagementInput}
                      onChange={(e) => setDateOfEngagementInput(e.target.value)}
                      slotProps={{ inputLabel: { shrink: true } }}
                      sx={{
                        '& input::-webkit-calendar-picker-indicator': {
                          cursor: 'pointer',
                        },
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Used for work anniversary milestones and leave calculations.
                    </Typography>
                  </Grid>

                  {/* Check-In Frequency */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <EventNoteIcon color="primary" fontSize="small" />
                      <Typography variant="subtitle2" color="text.secondary" fontWeight="500">
                        Check-In Frequency
                      </Typography>
                    </Box>
                    {checkInConfig?.managerOptedOutOfBuiltInCheckIns ? (
                      <Box>
                        <Typography variant="body1" fontWeight="500">
                          {employeeManagerOptedOutShortLabel}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {employeeManagerOptedOutMessage}
                        </Typography>
                      </Box>
                    ) : checkInConfig?.isActive ? (
                      <Box>
                        <Typography variant="body1" fontWeight="500">
                          {checkInConfig.frequency === 'Weekly' ? 'Weekly' :
                           checkInConfig.frequency === 'Fortnightly' ? 'Fortnightly' :
                           checkInConfig.frequency === 'Monthly' ? 'Monthly' :
                           checkInConfig.frequency}
                        </Typography>
                        {checkInConfig.nextCheckInDueDate && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            Next check-in: {formatDate(checkInConfig.nextCheckInDueDate)}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          Not configured
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          Ask your manager to set your check-in schedule on Team Roster.
                        </Typography>
                      </Box>
                    )}
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                      <Button
                        variant="contained"
                        onClick={handleProfileDatesUpdate}
                        disabled={savingProfileDates || !hasProfileDateChanges}
                      >
                        {savingProfileDates ? <CircularProgress size={24} color="inherit" /> : 'Save changes'}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Role Template Details Card */}
          {roleTemplate && (
            <Grid size={{ xs: 12, md: 4 }}>
              <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight="600" sx={{ mb: 2 }}>
                    Role Details
                  </Typography>
                  {roleTemplate.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {roleTemplate.description}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Chip
                      label={`${roleTemplate.objectiveMeasures?.length || 0} Objective Measure${
                        (roleTemplate.objectiveMeasures?.length || 0) !== 1 ? 's' : ''
                      }`}
                      variant="outlined"
                      size="small"
                    />
                    <Chip
                      label={`${roleTemplate.behaviouralMeasures?.length || 0} Behavioural Measure${
                        (roleTemplate.behaviouralMeasures?.length || 0) !== 1 ? 's' : ''
                      }`}
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>

        <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)', mt: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <AccessTimeIcon color="primary" fontSize="small" />
              <Typography variant="h6" fontWeight="600">
                {displayTimeZoneCopy.heading}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {displayTimeZoneCopy.helper}
            </Typography>
            <Autocomplete
              disableClearable
              options={timeZoneOptions}
              value={selectedTimeZoneOption}
              onChange={(_event, option) => {
                setSelectedTimeZoneId(option?.id ?? ORGANISATION_TIME_ZONE_VALUE);
              }}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField {...params} label={displayTimeZoneCopy.label} />
              )}
            />
            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Button variant="outlined" onClick={handleUseDeviceTimeZone} disabled={savingTimeZone}>
                {displayTimeZoneCopy.useDevice}
              </Button>
              <Button
                variant="contained"
                onClick={handleDisplayTimeZoneSave}
                disabled={savingTimeZone || !timeZoneDirty}
              >
                {savingTimeZone ? displayTimeZoneCopy.saving : displayTimeZoneCopy.save}
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Email Preferences Card */}
        <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)', mt: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <EmailIcon color="primary" fontSize="small" />
              <Typography variant="h6" fontWeight="600">
                Email Preferences
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose which email notifications you receive.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={emailPreferences.receiveReviewEmails}
                    onChange={(e) =>
                      setEmailPreferences((p) => ({ ...p, receiveReviewEmails: e.target.checked }))
                    }
                    disabled={!emailPreferences.receiveAllEmails}
                  />
                }
                label="Review emails (self-review, peer feedback, manager review, finalisation)"
              />
              <Typography variant="caption" display="block" color="text.secondary" sx={{ ml: 4, mt: -0.5, mb: 0.5 }}>
                Review cutoff reminders and peer reviewer reminders cannot be disabled.
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={emailPreferences.receiveCheckInEmails}
                    onChange={(e) =>
                      setEmailPreferences((p) => ({ ...p, receiveCheckInEmails: e.target.checked }))
                    }
                    disabled={!emailPreferences.receiveAllEmails}
                  />
                }
                label="Check-in emails (reminders, new scheduled, occurrence confirmed, manager decision)"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={emailPreferences.receiveIdpEmails}
                    onChange={(e) =>
                      setEmailPreferences((p) => ({ ...p, receiveIdpEmails: e.target.checked }))
                    }
                    disabled={!emailPreferences.receiveAllEmails}
                  />
                }
                label="IDP emails (approval reminders)"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={emailPreferences.receiveProfileChangeEmails}
                    onChange={(e) =>
                      setEmailPreferences((p) => ({
                        ...p,
                        receiveProfileChangeEmails: e.target.checked,
                      }))
                    }
                    disabled={!emailPreferences.receiveAllEmails}
                  />
                }
                label="Profile change emails (manager changes, role template, objectives)"
              />
              {EXPENSE_CLAIMS_ENABLED && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={emailPreferences.receiveExpenseClaimEmails}
                      onChange={(e) =>
                        setEmailPreferences((p) => ({
                          ...p,
                          receiveExpenseClaimEmails: e.target.checked,
                        }))
                      }
                      disabled={!emailPreferences.receiveAllEmails}
                    />
                  }
                  label="Expense claim emails (submit, approve, reject)"
                />
              )}
              <Divider sx={{ my: 1 }} />
              <FormControlLabel
                control={
                  <Switch
                    checked={!emailPreferences.receiveAllEmails}
                    onChange={(e) => {
                      const turnOffAll = e.target.checked;
                      setEmailPreferences((p) => ({
                        ...p,
                        receiveAllEmails: !turnOffAll,
                        ...(turnOffAll
                          ? {
                              receiveReviewEmails: false,
                              receiveCheckInEmails: false,
                              receiveIdpEmails: false,
                              receiveProfileChangeEmails: false,
                              receiveExpenseClaimEmails: false,
                            }
                          : {
                              receiveReviewEmails: true,
                              receiveCheckInEmails: true,
                              receiveIdpEmails: true,
                              receiveProfileChangeEmails: true,
                              receiveExpenseClaimEmails: true,
                            }),
                      }));
                      if (!turnOffAll) setAgreeToOptOutTerms(false);
                    }}
                  />
                }
                label="Turn off all optional emails"
              />
              {!emailPreferences.receiveAllEmails && (
                <Box
                  sx={{
                    ml: { xs: 0, sm: 4 },
                    mt: 1.5,
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'grey.100',
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    What you confirmed
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {emailPreferences.acknowledgmentText?.trim() || emailOptOutAcknowledgmentText}
                  </Typography>
                  {emailPreferences.emailOptOutAgreedAt && !savedReceiveAllEmails && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}
                    >
                      You confirmed this on{' '}
                      {formatDate(emailPreferences.emailOptOutAgreedAt, { includeTime: true })}.
                    </Typography>
                  )}
                  {savedReceiveAllEmails && (
                    <FormControlLabel
                      sx={{ mt: 1.5, ml: 0, alignItems: 'center' }}
                      control={
                        <Checkbox
                          checked={agreeToOptOutTerms}
                          onChange={(e) => setAgreeToOptOutTerms(e.target.checked)}
                        />
                      }
                      label="I have read and confirm the above."
                    />
                  )}
                </Box>
              )}
            </Box>
            <Button
              variant="contained"
              onClick={handleEmailPreferencesUpdate}
              disabled={
                updating ||
                (!emailPreferences.receiveAllEmails && savedReceiveAllEmails && !agreeToOptOutTerms)
              }
              sx={{ mt: 2 }}
            >
              {updating ? <CircularProgress size={24} /> : 'Save preferences'}
            </Button>
          </CardContent>
        </Card>
      </Box>
      {showLegacyHelp && (
        <ProfileHelpSidebar isExpanded={helpExpanded} onToggle={() => setHelpExpanded(!helpExpanded)} />
      )}
    </AppLayout>
  );
}
